import { useEffect, useRef, useMemo } from 'react'
import formatDate from 'date-fns/format'
import { formatTimelineData } from '@/adapters/format-timeline-data'
import { useQuery as useQueryBase } from '@maui/react-query'

// Expects an array of campaign objects with a 'timeline' property containing event points.
export const campaignToTimelineFormatter = (data) => {
  if (data === undefined) return
  // Until the response is corrected, one malware is an object vs array
  if (!Array.isArray(data)) data = [data]
  const formattedLineData = data.map((item, i) => {
    const { timeline } = item
    const filtered = timeline.filter(point => point.name && point.timestamp)
    const formatted = filtered.map((point, i) => {
      if (!point.timestamp) {
        return null
      }
      const date = new Date(point.timestamp)
      return {
        x: formatDate(date, 'M/d/yyyy'),
        y: 0,
        name: point.name || 'Activity Observed',
      }
    }).filter(Boolean)

    return { ...item, data: formatted }
  })

  return formattedLineData
}

export const compareByRecentDate = (a, b) => {
  const getRecentDate = obj => new Date(Math.max(...obj.data.map(dateObj => new Date(dateObj.x))))
  return getRecentDate(b) - getRecentDate(a)
}

export const useLiveRef = (value) => {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  })
  return ref
}

export const hasQueryKeys = ({ query, keys }) => {
  return keys.every(key => query[key] !== undefined)
}

export const transformTimelineData = (data) => {
  const formattedData = formatTimelineData(data)
  return { data: formattedData, unformatted: data }
}

/**
 * A custom hook that wraps the `useQuery` hook from `@maui/react-query` and allows for data transformation using an adapter function.
 * @param {Object} options - The options object for the `useQuery` hook, with an additional `adapters` property for data transformation.
 * @param {Object} options.adapters - An object containing adapter functions for transforming the data returned by the query.
 * @param {string} options.adapter - The name of the adapter function to use for transforming the data.
 * @returns {Object} - An object containing the transformed data and other properties returned by the `useQuery` hook.
 */
export const useQuery = (options = {}) => {
  const { adapters, adapter: adapterName, ...queryOptions } = options
  const { data: rawData, ...result } = useQueryBase(queryOptions)
  const adapterRef = useLiveRef(adapters ? adapters[adapterName] : null)
  const data = useMemo(() => {
    if (rawData === undefined && adapterRef.current === undefined) { return }
    return adapterRef.current?.(rawData) || rawData
  }, [rawData, adapterRef])
  return { data, ...result }
}

export const capitalize = (value) => {
  if (value) {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
  return ''
}

export const formatDateStringForDisplay = (dateString) => {
  const date = new Date(dateString)
  const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
  return date.toLocaleDateString(undefined, options)
}

export const fetchColor = (v) => {
  const value = v.toLowerCase()
  if (value) {
    if (value === 'critical' || value === 'wide' || value === 'yes') {
      return 'colorRiskCritical'
    } else if (value === 'high' || value === 'confirmed') {
      return 'colorRiskHigh'
    } else if (value === 'medium' || value === 'available') {
      return 'colorRiskMedium'
    } else if (value === 'low' || value === 'anticipated') {
      return 'colorRiskLow'
    } else if (value === 'no' || value === 'no known' || value === 'no data') {
      return 'colorRiskBenign'
    } else {
      return 'colorTextMain'
    }
  }
  return 'colorTextMain'
}
